/**
 * ContentCalendarPanel — top-level tab body for the Calendar sub-tab.
 *
 * Owns the CalendarMode state machine:
 *   A (strategy):   one strategy, full-span window
 *   B (cross-plan): N strategies, bounded window (1/2/4/8 weeks)
 *
 * Responsibilities:
 *   • Load the project's strategies list (real-time).
 *   • Assign each strategy a stable displayColor (persist on first read).
 *   • Subscribe to publications via the appropriate hook for the mode.
 *   • Subscribe to topic backlog once (for chip tooltips).
 *   • Sync current mode + window params to URL (calMode, wStart, wWeeks, sids, sid).
 *   • Open AddPublicationPopover and PlatformCadenceEditor.
 *   • Open TopicDetailPanel on chip click (deferred if the parent owns that dialog).
 *
 * This component is deliberately NOT a presentation component — all data
 * flow lives here, the child components are dumb renderers.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import { backfillReactivePublicationsFromSprints } from '@modules/content-plan/utils/backfillReactivePublicationsFromSprints';
import { ContentStrategyFirestoreService } from '../../services/ContentStrategyFirestoreService';
import { ContentPublicationFirestoreService } from '../../services/ContentPublicationFirestoreService';
import { backfillPublicationsFromPosts } from '../../utils/backfillPublicationsFromPosts';
import { useContentPublications } from '../../hooks/useContentPublications';
import { useMultiStrategyPublications } from '../../hooks/useMultiStrategyPublications';
import { useTopicBacklog } from '../../hooks/useTopicBacklog';
import { useStrategyPlatformCadence } from '../../hooks/useStrategyPlatformCadence';
import { useSprintPost } from '../../hooks/useSprintPosts';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { contentPostFirestoreService } from '../../services/ContentPostFirestoreService';
import { assignStrategyColors } from '../../utils/pickStrategyColor';
import { buildWeeks, moveScheduledAtToDay, weeksBetween } from '../../utils/calendarDates';
import { contentPostToPostLike, type PostLike } from '../../utils/postLike';
import UnifiedPostDialog from '../postLike/UnifiedPostDialog';
import type {
  CalendarAwareContentStrategy, ContentPost, ContentPublication, PlatformId,
} from '../../types';
import { ContentCalendarGrid } from './ContentCalendarGrid';
import { ContentCalendarToolbar, type CalendarToolbarMode } from './ContentCalendarToolbar';
import { StrategyLegend } from './StrategyLegend';
import { TopicBacklogSidebar } from './TopicBacklogSidebar';
import { CalendarTaskRegistryProvider } from './CalendarTaskRegistry';
import { AddPublicationPopover } from './AddPublicationPopover';
import { PlatformCadenceEditor } from './PlatformCadenceEditor';
import type { DayCellDropPayload } from './DayCell';

// ─── URL helpers ────────────────────────────────────────────────────

function readUrl(): URLSearchParams {
  return typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search);
}

function writeUrl(params: URLSearchParams) {
  if (typeof window === 'undefined') return;
  const next = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
  window.history.replaceState({}, '', next);
}

function readOpenPostId(): string | null {
  return readUrl().get('post');
}

function writeOpenPostId(postId?: string | null) {
  const params = readUrl();
  if (postId) {
    params.set('post', postId);
  } else {
    params.delete('post');
  }
  writeUrl(params);
}

// ─── Props ──────────────────────────────────────────────────────────

export interface ContentCalendarPanelProps {
  projectId: string;
  userId: string;
  /** `all` renders the read-only cross-author overview. */
  authorId: string | null;
  /** Optional — when provided, pins mode A to this strategy. */
  initialStrategyId?: string;
  /** When the user clicks a chip, parent may want to open an external dialog. */
  onOpenPublication?: (pub: ContentPublication) => void;
  /** DE2 — explicit user navigation to the Strategies sub-tab (empty state CTA). */
  onOpenStrategiesSubTab?: () => void;
  /** DE2 — opens the create-strategy dialog in place (empty state CTA). */
  onCreateStrategy?: () => void;
}

// ─── Component ──────────────────────────────────────────────────────

export const ContentCalendarPanel: React.FC<ContentCalendarPanelProps> = ({
  projectId, userId, authorId, initialStrategyId, onOpenPublication,
  onOpenStrategiesSubTab, onCreateStrategy,
}) => {
  const strategyService = useMemo(() => new ContentStrategyFirestoreService(), []);
  const readOnly = authorId === 'all' || !authorId;

  // ── Strategies list ──
  const [strategies, setStrategies] = useState<CalendarAwareContentStrategy[]>([]);
  const [loadingStrategies, setLoadingStrategies] = useState(true);
  useEffect(() => {
    const unsub = strategyService.subscribeStrategies(projectId, authorId, (list) => {
      setStrategies(list as CalendarAwareContentStrategy[]);
      setLoadingStrategies(false);
    });
    return () => unsub();
  }, [projectId, authorId, strategyService]);

  // ── Color palette (deterministic; persist on first read) ──
  const colorsByStrategy = useMemo(() => assignStrategyColors(strategies), [strategies]);
  useEffect(() => {
    // Persist any freshly-assigned colors that weren't yet stored. Best-effort.
    for (const s of strategies) {
      const assigned = colorsByStrategy[s.id];
      if (assigned && s.displayColor !== assigned) {
        strategyService.updateDisplayColor(projectId, s.id, assigned).catch(() => {
          // Non-blocking — color still renders from the in-memory palette.
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategies.length, projectId]);

  // ── URL-synced mode state ──
  const [mode, setMode] = useState<CalendarToolbarMode>('strategy');
  const [strategyId, setStrategyId] = useState<string>(initialStrategyId ?? '');
  const [windowStart, setWindowStart] = useState<Date>(() => new Date());
  const [weekCount, setWeekCount] = useState<number>(4);
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
  const [platformFilter, setPlatformFilter] = useState<Set<PlatformId>>(new Set());

  // Read URL once on mount + when strategies arrive for the first time.
  useEffect(() => {
    if (loadingStrategies) return;
    const u = readUrl();
    const urlMode = (u.get('calMode') as CalendarToolbarMode | null) ?? 'strategy';
    setMode(urlMode);

    if (urlMode === 'strategy') {
      const sid = u.get('sid') ?? initialStrategyId ?? strategies[0]?.id ?? '';
      setStrategyId(sid);
      setVisibleIds(new Set(sid ? [sid] : []));
    } else {
      const sids = (u.get('sids') ?? '').split(',').filter(Boolean);
      const effective = sids.length > 0 ? sids : strategies.slice(0, 3).map((s) => s.id);
      setVisibleIds(new Set(effective));
      const wStart = u.get('wStart');
      setWindowStart(wStart ? new Date(wStart) : new Date());
      const wWeeks = Number(u.get('wWeeks') ?? 4);
      setWeekCount(Number.isFinite(wWeeks) && wWeeks > 0 ? wWeeks : 4);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingStrategies]);

  // Write URL when state changes (after first mount).
  useEffect(() => {
    if (loadingStrategies) return;
    const u = readUrl();
    u.set('cpTab', 'calendar');
    u.set('calMode', mode);
    if (mode === 'strategy') {
      if (strategyId) u.set('sid', strategyId);
      else u.delete('sid');
      u.delete('sids'); u.delete('wStart'); u.delete('wWeeks');
    } else {
      u.set('sids', Array.from(visibleIds).join(','));
      u.set('wStart', windowStart.toISOString().slice(0, 10));
      u.set('wWeeks', String(weekCount));
      u.delete('sid');
    }
    writeUrl(u);
  }, [mode, strategyId, visibleIds, windowStart, weekCount, loadingStrategies]);

  // ── Active strategy (Mode A) ──
  const activeStrategy = useMemo(
    () => strategies.find((s) => s.id === strategyId) ?? null,
    [strategies, strategyId],
  );

  // ── Window derivation per mode ──
  const { renderedStart, renderedWeekCount, windowLabel } = useMemo(() => {
    if (mode === 'strategy' && activeStrategy) {
      const s = activeStrategy as CalendarAwareContentStrategy & { dateStart?: string; dateEnd?: string };
      const start = s.dateStart ? new Date(s.dateStart) : new Date();
      const end = s.dateEnd ? new Date(s.dateEnd) : new Date(start.getTime() + 28 * 86_400_000);
      const count = Math.max(1, weeksBetween(start, end));
      return {
        renderedStart: start,
        renderedWeekCount: count,
        windowLabel: `${s.dateStart ?? ''} — ${s.dateEnd ?? ''} · ${count} week${count > 1 ? 's' : ''}`,
      };
    }
    const weeks = buildWeeks(windowStart, weekCount);
    const label = `${weeks[0]?.label.split(' – ')[0] ?? ''} — ${weeks[weeks.length - 1]?.label.split(' – ')[1] ?? ''}`;
    return {
      renderedStart: windowStart,
      renderedWeekCount: weekCount,
      windowLabel: label,
    };
  }, [mode, activeStrategy, windowStart, weekCount]);

  // ── Publications subscription per mode ──
  const modeAPubs = useContentPublications(
    mode === 'strategy' ? projectId : null,
    mode === 'strategy' ? strategyId || null : null,
  );
  const modeBWindow = useMemo(
    () => (mode === 'cross-plan'
      ? { start: renderedStart, end: new Date(renderedStart.getTime() + renderedWeekCount * 7 * 86_400_000) }
      : null),
    [mode, renderedStart, renderedWeekCount],
  );
  const modeBPubs = useMultiStrategyPublications(
    mode === 'cross-plan' ? projectId : null,
    mode === 'cross-plan' ? Array.from(visibleIds) : [],
    modeBWindow,
  );

  const publications = mode === 'strategy' ? modeAPubs.publications : modeBPubs.publications;
  const platformOptions = useMemo(() => {
    const seen = new Set<string>();
    const add = (platform: unknown) => {
      const id = typeof platform === 'string' ? platform.trim() : '';
      if (id) seen.add(id);
    };
    const strategiesForMode = mode === 'strategy'
      ? (activeStrategy ? [activeStrategy] : [])
      : strategies.filter((strategy) => visibleIds.has(strategy.id));
    for (const strategy of strategiesForMode) {
      ((strategy as any).targetPlatforms ?? []).forEach(add);
      ((strategy as any).platformCadences ?? []).forEach((cadence: { platform?: unknown }) => add(cadence.platform));
    }
    publications.forEach((publication) => add(publication.platform));
    return Array.from(seen).sort() as PlatformId[];
  }, [activeStrategy, mode, publications, strategies, visibleIds]);

  useEffect(() => {
    if (platformOptions.length === 0) return;
    const optionSet = new Set(platformOptions.map(String));
    setPlatformFilter((current) => {
      const kept = Array.from(current).filter((platform) => optionSet.has(String(platform)));
      if (kept.length === current.size && current.size > 0) return current;
      return new Set(kept.length > 0 ? kept : platformOptions);
    });
  }, [platformOptions]);

  // Calendar backfill — one-shot lazy migration. Fires when Mode A's
  // active strategy has zero publications but its sprints already contain
  // posts (legacy data created before publications were emitted by the
  // pipeline). Runs ONCE per (strategyId) lifetime, gated by useRef so a
  // burst of subscription updates can't retrigger.
  const backfillAttempted = useRef<Set<string>>(new Set());
  const pubsCount = mode === 'strategy' ? modeAPubs.publications.length : 0;
  const pubsLoading = mode === 'strategy' ? modeAPubs.loading : true;
  useEffect(() => {
    if (mode !== 'strategy') return;
    if (!projectId || !strategyId) return;
    if (pubsLoading) return;
    if (pubsCount > 0) return;
    if (backfillAttempted.current.has(strategyId)) return;
    backfillAttempted.current.add(strategyId);

    const stratService = new ContentStrategyFirestoreService();
    const pubService = new ContentPublicationFirestoreService();
    void (async () => {
      try {
        const sprints = await stratService.listSprints(projectId, strategyId);
        if (sprints.length === 0) return;
        const postsArrays = await Promise.all(
          sprints.map(async (s) => {
            const posts = await stratService.listPostsForSprint(projectId, strategyId, s.id);
            return posts.map((post) => ({ sprintId: s.id, post }));
          }),
        );
        const flat = postsArrays.flat();
        if (flat.length === 0) return;
        const result = backfillPublicationsFromPosts({
          projectId,
          strategyId,
          posts: flat,
          existingPublications: [],
        });
        if (result.toCreate.length > 0) {
          const publicationAuthorId = (activeStrategy as any)?.authorId ?? authorId ?? undefined;
          await pubService.bulkCreate(
            projectId,
            strategyId,
            result.toCreate.map((publication) => ({ ...publication, authorId: publicationAuthorId } as ContentPublication)),
          );
          // eslint-disable-next-line no-console
          console.info('[Calendar backfill]', {
            strategyId,
            created: result.toCreate.length,
            skippedNoSchedule: result.skipped.missingSchedule,
          });
        }
      } catch (err) {
        // Non-fatal — backfill is best-effort. Log + leave the panel
        // in its current empty state. User can manually add chips.
        // eslint-disable-next-line no-console
        console.warn('[Calendar backfill] failed', err);
      }
    })();
  }, [mode, projectId, strategyId, pubsCount, pubsLoading, activeStrategy, authorId]);

  // Reactive slot repair — strategies created before the backend emitted
  // topic-less publications still carry `sprint.reactiveSlotsCount`. Fill
  // those missing publication docs when the calendar opens so old plans
  // render the manual slots too.
  const reactiveBackfillAttempted = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (mode !== 'strategy') return;
    if (!projectId || !strategyId || !activeStrategy) return;
    if (pubsLoading) return;
    if (platformOptions.length === 0) return;

    const key = `${strategyId}:${platformOptions.join(',')}`;
    if (reactiveBackfillAttempted.current.has(key)) return;
    reactiveBackfillAttempted.current.add(key);

    const stratService = new ContentStrategyFirestoreService();
    const pubService = new ContentPublicationFirestoreService();
    void (async () => {
      try {
        const sprints = await stratService.listSprints(projectId, strategyId);
        if (sprints.length === 0) return;
        const toCreate = backfillReactivePublicationsFromSprints({
          projectId,
          strategy: {
            id: activeStrategy.id,
            dateStart: (activeStrategy as unknown as { dateStart?: string | Date }).dateStart,
            platformCadences: (activeStrategy as { platformCadences?: unknown }).platformCadences,
          },
          sprints: sprints as any,
          targetPlatforms: platformOptions,
          existingPublications: publications.map((publication) => ({
            id: publication.id,
            platform: publication.platform,
            scheduledAt: publication.scheduledAt,
          })),
        });
        if (toCreate.length === 0) return;
        const publicationAuthorId = (activeStrategy as any)?.authorId ?? authorId ?? undefined;
        await pubService.bulkCreate(
          projectId,
          strategyId,
          toCreate.map((publication) => ({ ...publication, authorId: publicationAuthorId } as ContentPublication)),
        );
        // eslint-disable-next-line no-console
        console.info('[Calendar reactive backfill]', {
          strategyId,
          created: toCreate.length,
        });
      } catch (err) {
        // Non-fatal — backend generation now creates these for new plans.
        // eslint-disable-next-line no-console
        console.warn('[Calendar reactive backfill] failed', err);
      }
    })();
  }, [mode, projectId, strategyId, activeStrategy, pubsLoading, platformOptions, publications]);

  // Platform filter is a plain client-side pass over the merged list.
  // workflow-golden-tower — additionally sort by (topicId, aspectKey, scheduledAt)
  // so multi-aspect publications of the same topic appear adjacent inside a
  // day cell. Day cells use the same ordering when rendering their chips, so
  // visual disambiguation is "free" without changing the chip itself.
  const filteredPubs = useMemo(() => {
    const base = (platformFilter.size === 0 || platformFilter.size === platformOptions.length)
      ? publications
      : publications.filter((p) => platformFilter.has(p.platform));
    return [...base].sort((a, b) => {
      // Reactive (topicId-less) slots sink to the bottom of their day cell so
      // they don't visually split a topic's aspect group.
      const aTopic = a.topicId ?? '￿';
      const bTopic = b.topicId ?? '￿';
      if (aTopic !== bTopic) return aTopic.localeCompare(bTopic);
      const aAspect = (a as { aspectKey?: string }).aspectKey ?? '';
      const bAspect = (b as { aspectKey?: string }).aspectKey ?? '';
      if (aAspect !== bAspect) return aAspect.localeCompare(bAspect);
      return new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();
    });
  }, [publications, platformFilter, platformOptions.length]);

  // ── Topic backlog for chip titles/tooltips ──
  // Overview mode passes 'all'; subscribe unscoped there so titles still
  // resolve (useTopicBacklog returns [] for the 'all' sentinel).
  const { topics, loading: topicsLoading } = useTopicBacklog(
    projectId,
    authorId === 'all' ? null : authorId,
  );
  const topicTitlesByPub = useMemo(() => {
    const byId: Record<string, string | undefined> = {};
    const topicById: Record<string, string> = {};
    for (const t of topics) {
      // Human title resolution: explicit title/name, then the topic's angle
      // (canonical backlog display field). NEVER a bare id.
      const humanTitle = (t as { title?: string; name?: string }).title
        ?? (t as { name?: string }).name
        ?? (t as { angle?: string }).angle;
      if (t.id && humanTitle) topicById[t.id] = humanTitle;
    }
    for (const p of filteredPubs) {
      if (!p.topicId) continue;
      // Deleted/unknown topic → explicit fallback label (never the raw id).
      // While the backlog is still loading, leave undefined so the chip shows
      // its neutral placeholder instead of a false "deleted".
      byId[p.id] = topicById[p.topicId]
        ?? (topicsLoading ? undefined : 'Topic deleted');
    }
    return byId;
  }, [topics, topicsLoading, filteredPubs]);

  // ── Interactions ──
  const [addOpen, setAddOpen] = useState(false);
  const [addTarget, setAddTarget] = useState<{ date: Date | null; weekIndex?: number; weekdayIndex?: number }>({ date: null });
  const [cadenceOpen, setCadenceOpen] = useState(false);
  const [postDialogPublication, setPostDialogPublication] = useState<ContentPublication | null>(null);
  const { post: dialogPost } = useSprintPost(
    projectId,
    postDialogPublication?.strategyId ?? null,
    postDialogPublication?.sprintId ?? null,
    postDialogPublication?.contentPostId ?? null,
  );

  useEffect(() => {
    const openPostId = readOpenPostId();
    if (!openPostId || postDialogPublication?.contentPostId === openPostId) {
      return;
    }

    const matchedPublication = publications.find((publication) => publication.contentPostId === openPostId);
    if (matchedPublication) {
      setPostDialogPublication(matchedPublication);
    }
  }, [postDialogPublication?.contentPostId, publications]);

  useEffect(() => {
    if (postDialogPublication?.contentPostId) {
      writeOpenPostId(postDialogPublication.contentPostId);
    }
  }, [postDialogPublication?.contentPostId]);

  const handleAddClick = useCallback((date: Date, weekIndex: number, weekdayIndex: number) => {
    setAddTarget({ date, weekIndex, weekdayIndex });
    setAddOpen(true);
  }, []);

  const handleAddFromToolbar = useCallback(() => {
    setAddTarget({ date: new Date(), weekIndex: 0, weekdayIndex: new Date().getUTCDay() });
    setAddOpen(true);
  }, []);

  const handleCreatePublication = useCallback(
    async (data: Parameters<typeof modeAPubs.create>[0] & { strategyId: string }) => {
      if (readOnly) return;
      const publicationAuthorId = (strategies.find((strategy) => strategy.id === data.strategyId) as any)?.authorId
        ?? authorId
        ?? undefined;
      // Mode A: delegate to the already-bound hook. Mode B: we target the
      // selected strategy from the dialog directly via the service.
      if (mode === 'strategy' && data.strategyId === strategyId) {
        await modeAPubs.create({ ...data, authorId: publicationAuthorId } as any);
        return;
      }
      const service = new (await import('../../services/ContentPublicationFirestoreService')).ContentPublicationFirestoreService();
      await service.create(projectId, data.strategyId, {
        ...data,
        projectId,
        strategyId: data.strategyId,
        authorId: publicationAuthorId,
      } as any);
    },
    [mode, readOnly, strategies, authorId, strategyId, modeAPubs, projectId],
  );

  const handleDrop = useCallback(
    async (payload: DayCellDropPayload) => {
      if (readOnly) return;
      const pub = publications.find((p) => p.id === payload.publicationId);
      if (!pub) return;
      const scheduledAt = moveScheduledAtToDay(pub.scheduledAt, payload.targetDate);
      if (pub.scheduledAt === scheduledAt && pub.weekIndex === payload.targetWeekIndex && pub.weekdayIndex === payload.targetWeekdayIndex) {
        return;
      }
      const service = new (await import('../../services/ContentPublicationFirestoreService')).ContentPublicationFirestoreService();
      await service.move(projectId, pub.strategyId, pub.id, {
        scheduledAt,
        weekIndex: payload.targetWeekIndex,
        weekdayIndex: payload.targetWeekdayIndex,
      });
    },
    [publications, projectId, readOnly],
  );

  // H.2 — Topic dropped from TopicBacklogSidebar onto a slot. Behaviour:
  //   • If the slot already has a publication (the user re-targeted), set
  //     `topicId` on that publication.
  //   • Otherwise create a new ContentPublication slot with this topic
  //     for the active strategy (mode='strategy') or first visible strategy.
  const handleTopicDrop = useCallback(
    async (payload: { topicId: string; targetDate: Date; targetWeekIndex: number; targetWeekdayIndex: number }) => {
      if (readOnly) return;
      const targetStrategyId = mode === 'strategy'
        ? strategyId
        : Array.from(visibleIds)[0] ?? strategies[0]?.id;
      if (!targetStrategyId) return;
      const publicationAuthorId = (strategies.find((strategy) => strategy.id === targetStrategyId) as any)?.authorId
        ?? authorId
        ?? undefined;
      const service = new (await import('../../services/ContentPublicationFirestoreService')).ContentPublicationFirestoreService();
      const dateKeyStr = payload.targetDate.toISOString().slice(0, 10);
      const existing = publications.find(
        (p) => p.strategyId === targetStrategyId
          && p.scheduledAt.startsWith(dateKeyStr)
          && (!p.topicId || p.topicId === ''),
      );
      if (existing) {
        await service.update(projectId, existing.strategyId, existing.id, {
          topicId: payload.topicId,
          source: 'manual',
          authorId: publicationAuthorId,
        } as any);
        return;
      }
      const targetPlatform = platformOptions[0];
      if (!targetPlatform) return;
      const scheduledAt = `${dateKeyStr}T09:00:00.000Z`;
      await service.create(projectId, targetStrategyId, {
        projectId,
        strategyId: targetStrategyId,
        topicId: payload.topicId,
        platform: targetPlatform,
        scheduledAt,
        weekIndex: payload.targetWeekIndex,
        weekdayIndex: payload.targetWeekdayIndex,
        source: 'manual',
        status: 'planned',
        authorId: publicationAuthorId,
      } as any);
    },
    [mode, readOnly, strategyId, visibleIds, strategies, authorId, publications, projectId, platformOptions],
  );

  const handleChipClick = useCallback((pub: ContentPublication) => {
    if (pub.contentPostId) {
      setPostDialogPublication(pub);
      return;
    }
    onOpenPublication?.(pub);
  }, [onOpenPublication]);

  const handleClosePostDialog = useCallback(() => {
    setPostDialogPublication(null);
    writeOpenPostId(null);
  }, []);

  const handleLegendToggle = useCallback((sid: string, visible: boolean) => {
    setVisibleIds((prev) => {
      const next = new Set(prev);
      if (visible) next.add(sid); else next.delete(sid);
      return next;
    });
  }, []);

  const handleLegendSolo = useCallback((sid: string) => {
    setVisibleIds(new Set([sid]));
  }, []);

  const handleJumpToday = useCallback(() => {
    setWindowStart(new Date());
  }, []);

  const handlePrevWindow = useCallback(() => {
    setWindowStart((prev) => new Date(prev.getTime() - weekCount * 7 * 86_400_000));
  }, [weekCount]);

  const handleNextWindow = useCallback(() => {
    setWindowStart((prev) => new Date(prev.getTime() + weekCount * 7 * 86_400_000));
  }, [weekCount]);

  const handleModeChange = useCallback((next: CalendarToolbarMode) => {
    setMode(next);
    if (next === 'cross-plan' && visibleIds.size === 0) {
      setVisibleIds(new Set(strategies.slice(0, 3).map((s) => s.id)));
    }
  }, [visibleIds.size, strategies]);

  // ── Cadence editor wiring ──
  const cadenceHook = useStrategyPlatformCadence(projectId, activeStrategy);

  // ── Render ──
  if (loadingStrategies) {
    return <Box sx={{ p: 4 }}><Typography color="text.secondary">Loading strategies…</Typography></Box>;
  }
  if (strategies.length === 0) {
    // DE2 — actionable empty state: name the prerequisite AND carry the
    // fixing actions (create in place / explicit link to Strategies).
    return (
      <Alert
        severity="info"
        sx={{ m: 2 }}
        action={
          <Stack direction="row" spacing={1}>
            {onCreateStrategy && (
              <Button
                color="inherit"
                size="small"
                onClick={onCreateStrategy}
                data-testid={CONTENT_PLAN.CALENDAR_EMPTY_CREATE_STRATEGY_CTA}
              >
                Create a strategy
              </Button>
            )}
            {onOpenStrategiesSubTab && (
              <Button
                color="inherit"
                size="small"
                onClick={onOpenStrategiesSubTab}
                data-testid={CONTENT_PLAN.CALENDAR_EMPTY_OPEN_STRATEGIES_CTA}
              >
                Open Strategies
              </Button>
            )}
          </Stack>
        }
      >
        No strategies yet. Create a strategy to see its publications on the calendar.
      </Alert>
    );
  }

  const legendEntries = strategies.map((s) => ({ strategy: s, color: colorsByStrategy[s.id] }));

  return (
    <CalendarTaskRegistryProvider projectId={projectId}>
    <Box data-testid={CONTENT_PLAN.CALENDAR_PANEL} sx={{ px: 0.5, pb: 2 }}>
      <ContentCalendarToolbar
        mode={mode}
        onModeChange={handleModeChange}
        windowLabel={windowLabel}
        weekCount={renderedWeekCount}
        onWeekCountChange={setWeekCount}
        onPrevWindow={handlePrevWindow}
        onNextWindow={handleNextWindow}
        onJumpToday={handleJumpToday}
        platformFilter={platformFilter}
        platformOptions={platformOptions}
        onPlatformFilterChange={setPlatformFilter}
        onAddPublication={handleAddFromToolbar}
        readOnly={readOnly}
        canSwitchToCrossPlan={strategies.length > 0}
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: '100%', overflowX: 'auto' }}>
          <ContentCalendarGrid
            windowStart={renderedStart}
            weekCount={renderedWeekCount}
            publications={filteredPubs}
            colorsByStrategy={colorsByStrategy}
            topicTitlesByPub={topicTitlesByPub}
            detailLevel={mode === 'strategy' ? 'high' : 'compact'}
            onAddClick={readOnly ? undefined : handleAddClick}
            onChipClick={handleChipClick}
            onDrop={readOnly ? undefined : handleDrop}
            onTopicDrop={readOnly ? undefined : handleTopicDrop}
          />
        </Box>

        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
          {/* H.2 — Drag-source for topics. Drag any card onto a calendar
              slot to assign it. Cards are grouped by primary pillar. */}
          <Box sx={{ mb: 1.5, height: 320 }}>
            <TopicBacklogSidebar projectId={projectId} authorId={authorId} compact readOnly={readOnly} />
          </Box>
          <StrategyLegend
            entries={legendEntries}
            visibleIds={mode === 'strategy' ? new Set(strategyId ? [strategyId] : []) : visibleIds}
            onToggle={handleLegendToggle}
            onSolo={mode === 'cross-plan' ? handleLegendSolo : undefined}
            collapsed={mode === 'strategy'}
            allStrategies={mode === 'strategy' ? strategies : undefined}
            onSelect={mode === 'strategy' ? setStrategyId : undefined}
          />

          {mode === 'strategy' && activeStrategy && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Button
                size="small"
                startIcon={<TuneIcon />}
                variant="outlined"
                onClick={() => setCadenceOpen(true)}
                fullWidth
                disabled={readOnly}
              >
                Platform cadence
              </Button>
            </>
          )}
        </Box>
      </Stack>

      <AddPublicationPopover
        open={addOpen}
        onClose={() => setAddOpen(false)}
        targetDate={addTarget.date}
        targetWeekIndex={addTarget.weekIndex}
        targetWeekdayIndex={addTarget.weekdayIndex}
        strategies={strategies}
        platformOptions={platformOptions}
        defaultStrategyId={mode === 'strategy' ? strategyId : undefined}
        lockStrategy={mode === 'strategy'}
        onCreate={handleCreatePublication}
      />

      {activeStrategy && (
        <PlatformCadenceEditor
          open={cadenceOpen}
          onClose={() => setCadenceOpen(false)}
          initialCadences={cadenceHook.cadences}
          platformOptions={platformOptions}
          timeZone={(activeStrategy as CalendarAwareContentStrategy & { timeZone?: string }).timeZone}
          onSave={cadenceHook.saveCadences}
        />
      )}

      {postDialogPublication && dialogPost && (
        <UnifiedPostDialog
          open
          onClose={handleClosePostDialog}
          post={contentPostToPostLike(dialogPost as unknown as ContentPost, {
            kind: 'content_post',
            projectId,
            strategyId: postDialogPublication.strategyId,
            sprintId: postDialogPublication.sprintId,
          })}
          publicationId={postDialogPublication.id}
          publicationStrategyId={postDialogPublication.strategyId}
          publicationPlatform={postDialogPublication.platform}
          publicationScheduledAt={postDialogPublication.scheduledAt}
          publicationFacebookStatus={postDialogPublication.facebookPublish ?? null}
          publicationDelivery={postDialogPublication.delivery}
          readOnly={readOnly}
          onSave={async (next: PostLike) => {
            if (readOnly) return postDialogPublication.contentPostId ?? '';
            if (!postDialogPublication.contentPostId) throw new Error('contentPostId required');
            await contentPostFirestoreService.updateContent(projectId, postDialogPublication.contentPostId, {
              title: next.title,
              hook: next.hook,
              body: next.body,
              cta: next.cta,
              notes: next.notes,
              aiAssets: next.aiAssets,
            });
            return postDialogPublication.contentPostId;
          }}
          onRefine={async (userPrompt) => {
            if (readOnly) return;
            if (!postDialogPublication.contentPostId) throw new Error('contentPostId required');
            const client = new ContentPlanApiClient();
            const response = await client.refineContentPost({
              projectId,
              userId,
              authorId: (dialogPost as any)?.authorId ?? authorId ?? '',
              postId: postDialogPublication.contentPostId,
              userPrompt,
            });
            if (!response.success) throw new Error(response.error ?? 'Refine failed');
          }}
        />
      )}
    </Box>
    </CalendarTaskRegistryProvider>
  );
};
